import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ClipboardText, FloppyDisk, Building, House, Spinner } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

interface DueDiligenceDetailHeaderProps {
  buildingId?: string;
  unitId?: string;
  categoryName?: string;
  buildingName?: string;
  unitName?: string;
  onSave: () => void;
  isSaving: boolean;
  hasChanges: boolean;
}

/**
 * Header component for the Due Diligence detail page.
 * Shows breadcrumbs, title, and save button.
 */
const DueDiligenceDetailHeader: React.FC<DueDiligenceDetailHeaderProps> = ({
  buildingId,
  unitId,
  categoryName,
  buildingName,
  unitName,
  onSave,
  isSaving,
  hasChanges,
}) => {
  const navigate = useNavigate();
  const isUnitLevel = !!unitId;

  const handleBack = () => {
    // Navigate back to the building page with due-diligence tab
    if (buildingId) {
      navigate(`/building/${buildingId}/due-diligence`);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="sticky top-0 z-10 bg-white border-b border-neutral-200 shadow-sm">
      <div className="max-w-5xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Left side: Back button and breadcrumbs */}
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="text-neutral-secondary-mid hover:text-neutral-secondary-dark"
            >
              <ArrowLeft size={18} className="mr-1" />
              Terug
            </Button>

            {/* Breadcrumbs */}
            <nav className="flex items-center gap-2 text-sm">
              <span className="text-neutral-secondary-mid">
                {buildingName || `Gebouw ${buildingId}`}
              </span>
              <span className="text-neutral-300">/</span>
              {isUnitLevel && (
                <>
                  <span className="text-neutral-secondary-mid">
                    {unitName || `Unit ${unitId}`}
                  </span>
                  <span className="text-neutral-300">/</span>
                </>
              )}
              <span className="text-neutral-secondary-mid">Due Diligence</span>
              <span className="text-neutral-300">/</span>
              <span className="font-medium text-red-600">{categoryName || 'Categorie'}</span>
            </nav>
          </div>

          {/* Right side: Save button */}
          <div className="flex items-center gap-3">
            {hasChanges && (
              <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                Niet opgeslagen wijzigingen
              </span>
            )}
            <Button
              onClick={onSave}
              disabled={isSaving || !hasChanges}
              className="bg-red-600 hover:bg-red-700"
            >
              {isSaving ? (
                <Spinner size={16} className="animate-spin mr-2" />
              ) : (
                <FloppyDisk size={16} className="mr-2" />
              )}
              {isSaving ? 'Opslaan...' : 'Opslaan'}
            </Button>
          </div>
        </div>

        {/* Title row */}
        <div className="mt-4 flex items-center gap-3">
          {isUnitLevel ? (
            <House weight="duotone" size={28} className="text-red-500" />
          ) : (
            <Building weight="duotone" size={28} className="text-red-500" />
          )}
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <ClipboardText weight="duotone" size={24} className="text-red-500" />
              {categoryName || 'Due Diligence Categorie'}
            </h1>
            <p className="text-sm text-neutral-secondary-mid">
              {isUnitLevel
                ? `Unit-niveau controles voor ${unitName || unitId}`
                : `Gebouw-niveau controles voor ${buildingName || buildingId}`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DueDiligenceDetailHeader;
